import { useLocation } from 'react-router-dom';
import { Menu, Plus, Search, ShoppingCart } from 'lucide-react';
import { useProjects } from '../stores/projects.js';
import { MessagesBell } from './MessagesBell.js';
import { PendingBadge } from './PendingBadge.js';
import { Button } from '../ui/Button.js';
import { useUi } from '../stores/ui.js';
import { teinte } from '../lib/teinte.js';
import { ROUTES_AVEC_ENTETE } from './PageHeader.js';

// Une action primaire par vue (ADR 0007). Les vues tâches portent le même trio
// recherche / capture course / nouvelle tâche ; les autres ne portent que la leur,
// ou rien quand la page a déjà la sienne (Calories a un + flottant, le Catalogue ne
// s'édite pas sur mobile).
type Surface = 'tasks' | 'courses' | 'tracker' | 'none';

export function MobileTopBar(): React.ReactElement {
  const location = useLocation();
  const projects = useProjects((s) => s.projects);
  const openDrawer = useUi((s) => s.openDrawer);
  const openQuickAdd = useUi((s) => s.openQuickAdd);
  const openCourseAdd = useUi((s) => s.openCourseAdd);
  const openHabitForm = useUi((s) => s.openHabitForm);
  const openSearch = useUi((s) => s.openSearch);

  const { title, color } = deriveTitle(location.pathname, projects);
  const surface = deriveSurface(location.pathname);
  const projectMatch = location.pathname.match(/^\/p\/([^/]+)/);

  // `.barre` du kit : opaque, collante, et qui absorbe l'encoche. La primaire de la vue est le
  // seul bouton plein ; la capture de course, qui l'accompagne sur les vues tâches, est secondaire.
  // Marges inégales à dessein : le burger est discret, son glyphe flotte à 12 px dans sa cible et
  // paraît à 16 px du bord ; les boutons de droite sont encadrés, leur bord est le bord visible.
  return (
    <header className="barre gap-1 pl-1 pr-3">
      <Button variant="quiet" size="icon" onClick={openDrawer} aria-label="Open menu">
        <Menu size={20} aria-hidden />
      </Button>

      {/* Les vues à en-tête (`PageHeader`) disent leur titre en grand sous la barre : la barre
          ne le redit pas, elle ne porte que la navigation et les actions (Braise 2). */}
      <div className="flex-1 flex items-center justify-center gap-2 min-w-0 px-1">
        {!ROUTES_AVEC_ENTETE.includes(location.pathname) && (
          <>
            {color && <span className="point point-grand" style={teinte(color)} />}
            <h1 className="barre-titre truncate">{title}</h1>
          </>
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* La cloche et le compteur d'en-attente sont du chrome global : ils ne
            dépendent pas de la surface. */}
        <PendingBadge variant="icon" />
        <MessagesBell variant="icon" />

        {surface === 'tasks' && (
          <>
            <Button variant="quiet" size="icon" onClick={() => openSearch()} aria-label="Search">
              <Search size={20} aria-hidden />
            </Button>
            {/* Discret : une seule action pleine par barre, la nouvelle tâche. */}
            <Button
              variant="quiet"
              size="icon"
              onClick={() => openCourseAdd()}
              aria-label="Add to courses"
            >
              <ShoppingCart size={20} aria-hidden />
            </Button>
            <Button
              size="icon"
              onClick={() => openQuickAdd(projectMatch ? (projectMatch[1] ?? null) : null)}
              aria-label="New task"
            >
              <Plus size={20} aria-hidden />
            </Button>
          </>
        )}

        {surface === 'courses' && (
          <Button size="icon" onClick={() => openCourseAdd()} aria-label="Add to courses">
            <Plus size={20} aria-hidden />
          </Button>
        )}

        {surface === 'tracker' && (
          <Button size="icon" onClick={() => openHabitForm()} aria-label="New habit">
            <Plus size={20} aria-hidden />
          </Button>
        )}
      </div>
    </header>
  );
}

function deriveSurface(pathname: string): Surface {
  if (pathname === '/' || pathname === '/today' || pathname === '/board') return 'tasks';
  if (pathname.startsWith('/p/')) return 'tasks';
  if (pathname === '/courses') return 'courses';
  if (pathname === '/tracker') return 'tracker';
  return 'none';
}

function deriveTitle(
  pathname: string,
  projects: Array<{ projectId: string; name: string; color: string }>
): { title: string; color?: string } {
  if (pathname === '/today' || pathname === '/') return { title: 'Today' };
  if (pathname === '/board') return { title: 'Upcoming' };
  if (pathname === '/courses') return { title: 'Courses' };
  if (pathname === '/tracker') return { title: 'Tracker' };
  if (pathname === '/calories') return { title: 'Calories' };
  if (pathname === '/projets') return { title: 'Projets' };
  if (pathname.startsWith('/messages')) return { title: 'Messages' };
  const match = pathname.match(/^\/p\/([^/]+)/);
  if (match) {
    const project = projects.find((p) => p.projectId === match[1]);
    if (project) return { title: project.name, color: project.color };
    return { title: 'Project' };
  }
  return { title: 'nota' };
}
